import {
  ITEM_CATALOG_HASH,
  ITEM_CATALOG_VERSION,
  inventoryMassGrams,
  validateInventoryState,
  validateRuntimeStack,
  type ContainerCloseReason,
  type ContainerInventoryState,
  type DroppedItemEntity,
  type InventoryActionKind,
  type InventoryState,
  type InventorySlot,
  type ServerContainerClosed,
  type ServerContainerDelta,
  type ServerContainerSync,
  type ServerInventoryDelta,
  type ServerInventoryResult,
  type ServerInventorySync,
  type ServerWorldDropRemove,
  type ServerWorldDropSpawn,
  type ServerWorldDropSync,
  type ServerPhysicalSync, type ServerPhysicalResult, type PhysicalCarryState,
  validatePhysicalCarryState,
} from "@endclock/shared";

export type InventoryReplicationMessage =
  | ServerPhysicalSync
  | ServerPhysicalResult
  | ServerInventorySync
  | ServerInventoryDelta
  | ServerInventoryResult
  | ServerContainerSync
  | ServerContainerDelta
  | ServerContainerClosed
  | ServerWorldDropSync
  | ServerWorldDropSpawn
  | ServerWorldDropRemove;

export interface ReplicatedContainerState {
  readonly accessSessionId: string;
  readonly state: ContainerInventoryState;
}

export type InventoryResyncRequest =
  | {
    readonly kind: "INVENTORY";
    readonly inventoryId: string | null;
  }
  | {
    readonly kind: "CONTAINER";
    readonly accessSessionId: string;
    readonly containerObjectId: string;
  };

export interface ClientInventorySnapshot {
  readonly physical?: PhysicalCarryState | null;
  readonly physicalAt?: number;
  readonly physicalReceivedAt?: number;
  readonly physicalPending?: boolean;
  readonly physicalResult?: ServerPhysicalResult | null;
  readonly playerInventory: InventoryState | null;
  readonly container: ReplicatedContainerState | null;
  readonly worldDrops: ReadonlyMap<string, DroppedItemEntity>;
  readonly pendingRequests: ReadonlyMap<string, InventoryActionKind>;
  readonly results: ReadonlyMap<string, Readonly<ServerInventoryResult>>;
  readonly lastResult: Readonly<ServerInventoryResult> | null;
  readonly lastContainerCloseReason: ContainerCloseReason | null;
  readonly inventoryResyncPending: boolean;
  readonly containerResyncPending: boolean;
}

export interface ClientInventoryStoreOptions {
  readonly onResyncRequired?: (request: InventoryResyncRequest) => void;
}

export type ClientInventoryListener = (snapshot: ClientInventorySnapshot) => void;

const RESULT_HISTORY_LIMIT = 128;
const IDENTIFIER_PATTERN = /^[A-Za-z0-9:_-]+$/;

/**
 * Event-driven, non-authoritative view of item state received from the server.
 * A revision gap makes that replica unavailable for further deltas until a full
 * sync arrives, preventing a later out-of-order delta from hiding the gap.
 */
export class ClientInventoryStore {
  private physicalValue: PhysicalCarryState | null = null;
  private physicalAt = 0;
  private physicalReceivedAt = 0;
  private physicalPendingId: string | null = null;
  private physicalResultValue: ServerPhysicalResult | null = null;
  private playerInventoryValue: InventoryState | null = null;
  private containerValue: ReplicatedContainerState | null = null;
  private worldDropsValue: ReadonlyMap<string, DroppedItemEntity> = new Map();
  private pendingRequestsValue: ReadonlyMap<string, InventoryActionKind> = new Map();
  private resultsValue: ReadonlyMap<string, Readonly<ServerInventoryResult>> = new Map();
  private lastResultValue: Readonly<ServerInventoryResult> | null = null;
  private lastContainerCloseReasonValue: ContainerCloseReason | null = null;
  private inventoryResyncPendingValue = false;
  private containerResyncPendingValue = false;
  private readonly listeners = new Set<ClientInventoryListener>();
  private snapshotValue: ClientInventorySnapshot;

  constructor(private readonly options: ClientInventoryStoreOptions = {}) {
    this.snapshotValue = this.buildSnapshot();
  }

  snapshot(): ClientInventorySnapshot {
    return this.snapshotValue;
  }

  subscribe(listener: ClientInventoryListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  handle(message: InventoryReplicationMessage): boolean {
    switch (message.t) {
      case "physical_sync": {
        if (!Number.isFinite(message.at) || !validatePhysicalCarryState(message.state)) {
          this.options.onResyncRequired?.({ kind: "INVENTORY", inventoryId: this.playerInventoryValue?.inventoryId ?? null });
          return false;
        }
        if (this.physicalValue && message.state.revision < this.physicalValue.revision) return false;
        this.physicalValue = freezeTree(structuredClone(message.state));
        this.physicalAt = message.at;
        this.physicalReceivedAt = performance.now();
        this.publish();
        return true;
      }
      case "physical_result": {
        if (this.physicalPendingId === message.requestId) this.physicalPendingId = null;
        this.physicalResultValue = Object.freeze({ ...message });
        this.publish();
        return true;
      }
      case "inventory_sync": return this.handleInventorySync(message);
      case "inventory_delta": return this.handleInventoryDelta(message);
      case "inventory_result": return this.handleInventoryResult(message);
      case "container_sync": return this.handleContainerSync(message);
      case "container_delta": return this.handleContainerDelta(message);
      case "container_closed": return this.handleContainerClosed(message);
      case "world_drop_sync": return this.handleWorldDropSync(message);
      case "world_drop_spawn": return this.handleWorldDropSpawn(message);
      case "world_drop_remove": return this.handleWorldDropRemove(message);
    }
  }

  markPending(requestId: string, action: InventoryActionKind): boolean {
    if (!isIdentifier(requestId, 64) || this.pendingRequestsValue.has(requestId)) return false;
    const pending = new Map(this.pendingRequestsValue);
    pending.set(requestId, action);
    this.pendingRequestsValue = pending;
    if (this.resultsValue.has(requestId)) {
      const results = new Map(this.resultsValue);
      results.delete(requestId);
      this.resultsValue = results;
      if (this.lastResultValue?.requestId === requestId) this.lastResultValue = null;
    }
    this.publish();
    return true;
  }

  markPhysicalPending(requestId: string): boolean {
    if (this.physicalPendingId || !this.physicalValue) return false;
    this.physicalPendingId = requestId;
    this.physicalResultValue = null;
    this.publish();
    return true;
  }

  handleInventorySync(message: ServerInventorySync): boolean {
    if (
      message.catalogVersion !== ITEM_CATALOG_VERSION
      || message.catalogHash !== ITEM_CATALOG_HASH
      || !isValidInventoryState(message.state)
    ) {
      return this.requireInventoryResync(message.state.inventoryId || null);
    }
    this.playerInventoryValue = cloneInventoryState(message.state);
    this.inventoryResyncPendingValue = false;
    this.publish();
    return true;
  }

  handleInventoryDelta(message: ServerInventoryDelta): boolean {
    const current = this.playerInventoryValue;
    if (current && message.inventoryId !== current.inventoryId) return false;
    if (current && message.revision <= current.revision) return false;
    if (!current || this.inventoryResyncPendingValue || message.revision !== current.revision + 1) {
      return this.requireInventoryResync(current?.inventoryId ?? message.inventoryId);
    }
    const slots = applySlotChanges(current.slots, message.changedSlots);
    if (!slots) return this.requireInventoryResync(current.inventoryId);
    const next: InventoryState = {
      ...current,
      revision: message.revision,
      massGrams: message.massGrams,
      slots,
    };
    if (!isValidInventoryState(next)) return this.requireInventoryResync(current.inventoryId);
    this.playerInventoryValue = cloneInventoryState(next);
    this.publish();
    return true;
  }

  handleInventoryResult(message: ServerInventoryResult): boolean {
    const stored = Object.freeze({ ...message });
    const previous = this.resultsValue.get(message.requestId);
    const wasPending = this.pendingRequestsValue.has(message.requestId);
    if (!wasPending && previous && sameResult(previous, stored)) return false;

    if (wasPending) {
      const pending = new Map(this.pendingRequestsValue);
      pending.delete(message.requestId);
      this.pendingRequestsValue = pending;
    }
    const results = new Map(this.resultsValue);
    results.delete(message.requestId);
    results.set(message.requestId, stored);
    while (results.size > RESULT_HISTORY_LIMIT) {
      const oldest = results.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      results.delete(oldest);
    }
    this.resultsValue = results;
    this.lastResultValue = stored;
    this.publish();
    return true;
  }

  handleContainerSync(message: ServerContainerSync): boolean {
    if (!isIdentifier(message.accessSessionId, 96) || !isValidContainerState(message.state)) {
      return this.requireContainerResync(
        message.accessSessionId,
        message.state.containerObjectId,
      );
    }
    this.containerValue = Object.freeze({
      accessSessionId: message.accessSessionId,
      state: cloneContainerState(message.state),
    });
    this.containerResyncPendingValue = false;
    this.lastContainerCloseReasonValue = null;
    this.publish();
    return true;
  }

  handleContainerDelta(message: ServerContainerDelta): boolean {
    const current = this.containerValue;
    if (!current) return false;
    if (
      message.accessSessionId !== current.accessSessionId
      || message.containerObjectId !== current.state.containerObjectId
    ) return false;
    if (message.revision <= current.state.revision) return false;
    if (this.containerResyncPendingValue || message.revision !== current.state.revision + 1) {
      return this.requireContainerResync(current.accessSessionId, current.state.containerObjectId);
    }
    const slots = applySlotChanges(current.state.slots, message.changedSlots);
    if (!slots) return this.requireContainerResync(current.accessSessionId, current.state.containerObjectId);
    const next: ContainerInventoryState = {
      ...current.state,
      revision: message.revision,
      massGrams: inventoryMassGrams(slots),
      slots,
    };
    if (!isValidContainerState(next)) {
      return this.requireContainerResync(current.accessSessionId, current.state.containerObjectId);
    }
    this.containerValue = Object.freeze({
      accessSessionId: current.accessSessionId,
      state: cloneContainerState(next),
    });
    this.publish();
    return true;
  }

  handleContainerClosed(message: ServerContainerClosed): boolean {
    if (!this.containerValue || message.accessSessionId !== this.containerValue.accessSessionId) return false;
    this.containerValue = null;
    this.containerResyncPendingValue = false;
    this.lastContainerCloseReasonValue = message.reason;
    this.publish();
    return true;
  }

  handleWorldDropSync(message: ServerWorldDropSync): boolean {
    const drops = new Map<string, DroppedItemEntity>();
    for (const entity of message.entities) {
      if (!isValidDroppedItem(entity) || drops.has(entity.entityId)) return false;
      drops.set(entity.entityId, cloneDroppedItem(entity));
    }
    this.worldDropsValue = drops;
    this.publish();
    return true;
  }

  handleWorldDropSpawn(message: ServerWorldDropSpawn): boolean {
    if (!isValidDroppedItem(message.entity)) return false;
    const previous = this.worldDropsValue.get(message.entity.entityId);
    if (previous && previous.revision >= message.entity.revision) return false;
    const drops = new Map(this.worldDropsValue);
    drops.set(message.entity.entityId, cloneDroppedItem(message.entity));
    this.worldDropsValue = drops;
    this.publish();
    return true;
  }

  handleWorldDropRemove(message: ServerWorldDropRemove): boolean {
    if (!this.worldDropsValue.has(message.entityId)) return false;
    const drops = new Map(this.worldDropsValue);
    drops.delete(message.entityId);
    this.worldDropsValue = drops;
    this.publish();
    return true;
  }

  reset(): boolean {
    const changed = this.physicalValue !== null || this.playerInventoryValue !== null
      || this.containerValue !== null
      || this.worldDropsValue.size > 0
      || this.pendingRequestsValue.size > 0
      || this.resultsValue.size > 0
      || this.lastResultValue !== null
      || this.lastContainerCloseReasonValue !== null
      || this.inventoryResyncPendingValue
      || this.containerResyncPendingValue;
    this.playerInventoryValue = null;
    this.physicalValue = null;
    this.physicalAt = 0;
    this.physicalReceivedAt = 0;
    this.physicalPendingId = null;
    this.physicalResultValue = null;
    this.containerValue = null;
    this.worldDropsValue = new Map();
    this.pendingRequestsValue = new Map();
    this.resultsValue = new Map();
    this.lastResultValue = null;
    this.lastContainerCloseReasonValue = null;
    this.inventoryResyncPendingValue = false;
    this.containerResyncPendingValue = false;
    if (changed) this.publish();
    return changed;
  }

  private requireInventoryResync(inventoryId: string | null): boolean {
    if (this.inventoryResyncPendingValue) return false;
    this.inventoryResyncPendingValue = true;
    this.publish();
    this.options.onResyncRequired?.(Object.freeze({ kind: "INVENTORY", inventoryId }));
    return true;
  }

  private requireContainerResync(accessSessionId: string, containerObjectId: string): boolean {
    if (this.containerResyncPendingValue) return false;
    this.containerResyncPendingValue = true;
    this.publish();
    this.options.onResyncRequired?.(Object.freeze({
      kind: "CONTAINER",
      accessSessionId,
      containerObjectId,
    }));
    return true;
  }

  private publish(): void {
    this.snapshotValue = this.buildSnapshot();
    for (const listener of [...this.listeners]) listener(this.snapshotValue);
  }

  private buildSnapshot(): ClientInventorySnapshot {
    return Object.freeze({
      physical: this.physicalValue,
      physicalAt: this.physicalAt,
      physicalReceivedAt: this.physicalReceivedAt,
      physicalPending: this.physicalPendingId !== null,
      physicalResult: this.physicalResultValue,
      playerInventory: this.playerInventoryValue,
      container: this.containerValue,
      worldDrops: this.worldDropsValue,
      pendingRequests: this.pendingRequestsValue,
      results: this.resultsValue,
      lastResult: this.lastResultValue,
      lastContainerCloseReason: this.lastContainerCloseReasonValue,
      inventoryResyncPending: this.inventoryResyncPendingValue,
      containerResyncPending: this.containerResyncPendingValue,
    });
  }
}

function freezeTree<T>(value: T): T {
  if (value && typeof value === "object") {
    for (const child of Object.values(value)) freezeTree(child);
    Object.freeze(value);
  }
  return value;
}

function applySlotChanges(
  current: readonly InventorySlot[],
  changes: readonly (readonly [slot: number, stack: InventorySlot])[],
): readonly InventorySlot[] | null {
  const slots = [...current];
  const changedIndexes = new Set<number>();
  for (const [index, stack] of changes) {
    if (!Number.isSafeInteger(index) || index < 0 || index >= slots.length || changedIndexes.has(index)) return null;
    if (stack !== null && !validateRuntimeStack(stack)) return null;
    changedIndexes.add(index);
    slots[index] = cloneStack(stack);
  }
  return Object.freeze(slots);
}

function cloneInventoryState(state: InventoryState): InventoryState {
  return Object.freeze({
    ...state,
    slots: Object.freeze(state.slots.map(cloneStack)),
  });
}

function cloneContainerState(state: ContainerInventoryState): ContainerInventoryState {
  return Object.freeze({
    ...state,
    slots: Object.freeze(state.slots.map(cloneStack)),
  });
}

function cloneStack(stack: InventorySlot): InventorySlot {
  return stack === null ? null : Object.freeze({ ...stack });
}

function cloneDroppedItem(entity: DroppedItemEntity): DroppedItemEntity {
  return Object.freeze({
    ...entity,
    stack: Object.freeze({ ...entity.stack }),
  });
}

function isValidInventoryState(state: InventoryState): boolean {
  return isIdentifier(state.inventoryId, 128)
    && validateInventoryState(state)
    && hasUniqueStackIds(state.slots);
}

function isValidContainerState(state: ContainerInventoryState): boolean {
  return isValidInventoryState(state)
    && isIdentifier(state.containerObjectId, 192)
    && isIdentifier(state.profileId, 96);
}

function hasUniqueStackIds(slots: readonly InventorySlot[]): boolean {
  const ids = new Set<string>();
  for (const stack of slots) {
    if (!stack) continue;
    if (ids.has(stack.instanceId)) return false;
    ids.add(stack.instanceId);
  }
  return true;
}

function isValidDroppedItem(entity: DroppedItemEntity): boolean {
  return isIdentifier(entity.entityId, 96)
    && validateRuntimeStack(entity.stack)
    && Number.isFinite(entity.x)
    && Number.isFinite(entity.y)
    && Number.isFinite(entity.z)
    && Number.isFinite(entity.yaw)
    && Number.isFinite(entity.createdAt)
    && Number.isFinite(entity.expiresAt)
    && entity.expiresAt >= entity.createdAt
    && Number.isSafeInteger(entity.revision)
    && entity.revision >= 0;
}

function isIdentifier(value: string, maximumLength: number): boolean {
  return value.length > 0 && value.length <= maximumLength && IDENTIFIER_PATTERN.test(value);
}

function sameResult(
  left: Readonly<ServerInventoryResult>,
  right: Readonly<ServerInventoryResult>,
): boolean {
  return left.requestId === right.requestId
    && left.action === right.action
    && left.result === right.result
    && left.inventoryRevision === right.inventoryRevision
    && left.containerRevision === right.containerRevision
    && left.entityId === right.entityId;
}
